/**
 * Agent Detail — Full detail view with 6 internal tabs
 *
 * Tabs: Overview, Tools & Skills, Test, Executions, Analytics, Settings
 * Cross-links to Studio tools/skills.
 * Uses MarkdownRenderer for system prompt display.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  ArrowLeft, Settings2, Wrench, BookOpen, Play, Pause,
  History, BarChart3, Settings, Copy, Bot, Lock,
  ExternalLink, CheckCircle, XCircle, Clock, Zap,
  Send, MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { AgentStatusBadge, OwnershipBadge, AutonomyBadge, TriggerBadges } from '@/components/agents';
import { AgentIcon } from '@/components/shared/icon-picker';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AGENT_CATEGORY_LABELS,
  getAgentCategoryStyle,
  type MockAgent,
  type AgentStatus,
} from '@/lib/mock-agents-data';
import {
  useAgent,
  useAgentExecutions,
  useUpdateAgent,
  useDeleteAgent,
} from '@/lib/use-agents';

export const Route = createFileRoute('/agents/$agentId/')({
  component: AgentDetailPage,
});

type DetailTab = 'overview' | 'tools' | 'test' | 'executions' | 'analytics' | 'settings';

function AgentDetailPage() {
  const { agentId } = Route.useParams();
  const navigate = useNavigate();
  // Real-data: custom_agents detail + agent_invocations history.
  const { data: realAgent } = useAgent(agentId);
  const agent = realAgent ?? null;
  const { data: realExecutions = [] } = useAgentExecutions(agentId);
  const MOCK_EXECUTIONS = realExecutions;
  const MOCK_EXECUTION_STEPS: Record<string, unknown[]> = {};
  void MOCK_EXECUTIONS; void MOCK_EXECUTION_STEPS;
  // Cluster C-STUB-TOAST (Pod-S) — Danger Zone mutations. Pre-fix both
  // buttons only fired a bare toast.success and the row stayed in DB.
  const updateAgent = useUpdateAgent();
  const deleteAgent = useDeleteAgent();
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  // fix-wave-4 PR-AG (Bug #1040) — pre-fix `useState(agent?.status ?? 'active')`
  // only ran once on first render when `agent` was null (TanStack Query
  // hadn't resolved yet). The state never updated when the real agent
  // arrived, so a draft custom agent rendered the header toggle as
  // "Pause" instead of "Activate". Sync to the real status when it
  // becomes available.
  const [status, setStatus] = useState<AgentStatus>(agent?.status ?? 'active');
  useEffect(() => {
    if (agent?.status) setStatus(agent.status);
  }, [agent?.status]);

  // Test tab state
  const [testInput, setTestInput] = useState('');
  const [testMessages, setTestMessages] = useState<{ role: 'user' | 'agent'; content: string }[]>([]);
  const [testRunning, setTestRunning] = useState(false);

  // System prompt collapsed state
  const [promptOpen, setPromptOpen] = useState(false);

  if (!agent) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Bot className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h2 className="text-lg font-semibold">Agent Not Found</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">No agent with ID "{agentId}"</p>
        <Button variant="outline" onClick={() => navigate({ to: '/agents' })}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Agents
        </Button>
      </div>
    );
  }

  // fix-wave-4 PR-AG (Bug #1040 + #1038) — wire the header toggle to the
  // real PATCH endpoint. Pre-fix the button only updated local state +
  // fired a toast; the row never changed server-side, so refreshing the
  // page reverted to the original state. The matrix (AGENTS-DETAIL-005/
  // 006) requires the lifecycle PATCH to persist.
  const handleToggleStatus = () => {
    const next = status === 'active' ? 'paused' : 'active';
    updateAgent.mutate(
      { id: agent.id, body: { status: next as 'active' | 'paused' } },
      {
        onSuccess: () => {
          setStatus(next as AgentStatus);
          toast.success(`${agent.name} ${next}`);
        },
        onError: (e: Error) => toast.error(`${next} failed: ${e.message}`),
      },
    );
  };

  const handleTestSend = () => {
    if (!testInput.trim() || testRunning) return;
    setTestMessages(prev => [...prev, { role: 'user', content: testInput }]);
    setTestInput('');
    setTestRunning(true);
    setTimeout(() => {
      // Pod-BBB #768 AGENTS-RUNTIME-001 — pre-fix this returned a
      // fabricated grounded answer (an invented tools/skills count, a
      // fake document citation, an invented telemetry window, and a
      // hardcoded confidence percentage) via setTimeout, with NO real
      // retrieval, tool execution, or model call. CLAUDE.md §5.3 #14-15
      // ("never guess when uncertain; every claim must be verified
      // against its cited source") and founder-feedback
      // `feedback_no_mock_data_in_prod` require an honest message
      // when the test runtime is not wired. The in-Hub Test tab
      // remains a planned surface — until a real /agents/:id/test
      // streaming endpoint lands, the response below names the gap and
      // routes users to the production Ask Argus surface where the
      // agent IS actually invokable (via @mention or via the agent
      // picker in the chat composer).
      setTestMessages(prev => [...prev, {
        role: 'agent',
        content:
          `Test runtime is not available for this agent yet.\n\n` +
          `The in-Hub Test tab does not execute the agent — no tools, ` +
          `skills, retrieval, or model calls run from here. To invoke ` +
          `**${agent.name}** with grounded answers, citations, and the ` +
          `configured tool set, open Ask Argus and either:\n\n` +
          `- Type \`@${agent.name}\` to dispatch the agent directly, or\n` +
          `- Pick the agent in the composer's specialist menu.\n\n` +
          `A dedicated \`POST /agents/{id}/test\` streaming endpoint is ` +
          `tracked separately; this notice will be replaced when the ` +
          `runtime ships.`,
      }]);
      setTestRunning(false);
    }, 400);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Back to Agents" onClick={() => navigate({ to: '/agents' })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <AgentIcon iconName={agent.icon} colorName={agent.iconColor} size="md" />
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold">{agent.name}</h1>
            <p className="text-xs text-muted-foreground truncate">{agent.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <AgentStatusBadge status={status} />
            <OwnershipBadge ownership={agent.ownership} />
            <Badge variant="secondary" className="text-[9px]">v{agent.version}</Badge>
            {!agent.isBuiltin && (
              <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={handleToggleStatus}>
                {status === 'active' ? <><Pause className="h-3 w-3" /> Pause</> : <><Play className="h-3 w-3" /> Activate</>}
              </Button>
            )}
            <Button size="sm" className="gap-1 h-7 text-xs" onClick={() => navigate({ to: '/agents/$agentId/edit', params: { agentId: agent.id } })}>
              <Settings2 className="h-3 w-3" /> Edit
            </Button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 mt-3">
          {([
            { id: 'overview' as DetailTab, label: 'Overview', icon: Bot },
            { id: 'tools' as DetailTab, label: 'Tools & Skills', icon: Wrench },
            { id: 'test' as DetailTab, label: 'Test', icon: Play },
            { id: 'executions' as DetailTab, label: 'Executions', icon: History },
            { id: 'analytics' as DetailTab, label: 'Analytics', icon: BarChart3 },
            { id: 'settings' as DetailTab, label: 'Settings', icon: Settings },
          ]).map(tab => (
            <Button key={tab.id} variant={activeTab === tab.id ? 'secondary' : 'ghost'} size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setActiveTab(tab.id)}>
              <tab.icon className="h-3.5 w-3.5" /> {tab.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 max-w-5xl space-y-6">
        {activeTab === 'overview' && (
          <>
            {/* Identity */}
            <Card>
              <CardContent className="pt-4 grid grid-cols-2 gap-4">
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Category</p><Badge variant="outline" className={cn('text-[9px]', getAgentCategoryStyle(agent.category).color)}>{getAgentCategoryStyle(agent.category).label}</Badge></div>
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Model</p><p className="text-sm font-mono">{agent.modelId}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Autonomy</p><AutonomyBadge autonomy={agent.autonomyLevel} /></div>
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Guardrails</p><Badge variant="outline" className="text-[9px]">{agent.guardrailProfile}</Badge></div>
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Max Steps</p><p className="text-sm">{agent.maxSteps}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Triggers</p><TriggerBadges triggers={agent.triggerModes} /></div>
              </CardContent>
            </Card>

            {/* System Prompt */}
            <Collapsible open={promptOpen} onOpenChange={setPromptOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">System Prompt</CardTitle>
                      <Badge variant="secondary" className="text-[9px]">{promptOpen ? 'Collapse' : 'Expand'}</Badge>
                    </div>
                    <CardDescription className="text-xs">{agent.systemPrompt.split('\n')[0].substring(0, 100)}...</CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    <div className="bg-muted/30 rounded-lg p-4 prose prose-sm dark:prose-invert max-w-none">
                      <MarkdownRenderer content={agent.systemPrompt} />
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-lg font-bold">{agent.metrics.totalQueries.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">Total Queries</p></CardContent></Card>
              <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-lg font-bold">{agent.metrics.avgResponseMs}ms</p><p className="text-[10px] text-muted-foreground">Avg Response</p></CardContent></Card>
              <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-lg font-bold">{Math.round(agent.metrics.successRate * 100)}%</p><p className="text-[10px] text-muted-foreground">Success Rate</p></CardContent></Card>
              <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-lg font-bold">{Math.round(agent.metrics.satisfactionScore * 100)}%</p><p className="text-[10px] text-muted-foreground">Satisfaction</p></CardContent></Card>
            </div>
          </>
        )}

        {activeTab === 'tools' && (
          <>
            {/* Tools */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><Wrench className="h-4 w-4" /> Tools ({agent.tools.length})</h3>
              <div className="space-y-1.5">
                {agent.tools.map(tool => (
                  <div key={tool} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <Wrench className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="text-xs font-mono flex-1">{tool}</span>
                    {/* #583 AGENTS-DETAIL-016 — pre-fix this navigated to
                        plain `/studio` which resolves to the Models tab.
                        Point at the Tools surface so tool inspection is
                        one click away (sibling fix matches the skill row's
                        `/studio/skills` target). */}
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" title="View in Studio" onClick={() => navigate({ to: '/studio/tools' })}>
                      <ExternalLink className="h-2.5 w-2.5" /> Studio
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Skills */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><BookOpen className="h-4 w-4" /> Skills ({agent.skills.length})</h3>
              <div className="space-y-1.5">
                {agent.skills.map(skill => (
                  <div key={skill} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <BookOpen className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="text-xs font-mono flex-1">{skill}</span>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" title="View in Studio" onClick={() => navigate({ to: '/studio/skills' })}>
                      <ExternalLink className="h-2.5 w-2.5" /> Studio
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'test' && (
          <div className="flex flex-col h-[500px] border rounded-lg">
            {/* Chat messages */}
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {testMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="h-8 w-8 text-muted-foreground/20 mb-2" />
                  <p className="text-sm text-muted-foreground">Send a message to test this agent</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">The agent will use its configured tools and skills to respond</p>
                </div>
              )}
              {testMessages.map((msg, i) => (
                <div key={i} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div className={cn('max-w-[80%] rounded-2xl px-4 py-2.5', msg.role === 'user' ? 'bg-primary text-primary-foreground rounded-tr-sm' : 'bg-muted/40 rounded-tl-sm')}>
                    {msg.role === 'agent' ? (
                      <MarkdownRenderer content={msg.content} />
                    ) : (
                      <p className="text-sm">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}
              {testRunning && (
                <div className="flex gap-2.5">
                  <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Input */}
            <div className="p-3 border-t flex gap-2">
              <Input value={testInput} onChange={e => setTestInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTestSend(); } }} placeholder="Type a test message..." className="text-sm" disabled={testRunning} />
              <Button onClick={handleTestSend} disabled={!testInput.trim() || testRunning} size="icon" className="h-9 w-9 shrink-0" aria-label="Send test message" title="Send">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'executions' && (
          <div className="rounded-lg border overflow-x-auto">
            <div className="min-w-[700px]">
              <div className="grid grid-cols-[1fr_80px_80px_60px_120px] gap-3 p-3 bg-muted/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                <span>Query</span>
                <span>Status</span>
                <span className="text-right">Duration</span>
                <span className="text-center">Tools</span>
                <span>Timestamp</span>
              </div>
              {/* fix-wave-4 PR-AG (Bug #973 AGENTS-DETAIL-025) — explicit
                  empty state. Pre-fix the tab rendered only the headers
                  when invocations were empty; matrix expected a "no
                  executions" disclosure. */}
              {MOCK_EXECUTIONS.length === 0 && (
                <div className="p-6 text-center text-xs text-muted-foreground">
                  <History className="h-6 w-6 mx-auto mb-2 text-muted-foreground/30" />
                  No executions yet — this agent hasn't been invoked.
                </div>
              )}
              <div className="divide-y">
                {MOCK_EXECUTIONS.map(exec => (
                  <div key={exec.id} className="grid grid-cols-[1fr_80px_80px_60px_120px] gap-3 p-3 items-center hover:bg-muted/30 transition-colors">
                    <p className="text-xs truncate">{exec.query}</p>
                    <Badge variant="outline" className={cn('text-[9px] w-fit', exec.status === 'success' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : exec.status === 'error' ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400')}>
                      {exec.status === 'success' ? <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> : exec.status === 'error' ? <XCircle className="h-2.5 w-2.5 mr-0.5" /> : <Clock className="h-2.5 w-2.5 mr-0.5" />}
                      {exec.status}
                    </Badge>
                    <p className="text-xs text-right">{(exec.durationMs / 1000).toFixed(1)}s</p>
                    <p className="text-xs text-center">{exec.toolCalls}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(exec.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <>
            {/* fix-wave-4 PR-AG (Bug #973 AGENTS-DETAIL-029) — analytics
                tab label + empty-state disclosure. Pre-fix the matrix
                couldn't detect a "metrics" or "no analytics" header, so
                the tab read as blank. Both are present below. */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Analytics</h3>
              {agent.metrics.totalQueries === 0 && (
                <span className="text-[10px] text-muted-foreground">No analytics yet — agent has zero invocations.</span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-lg font-bold">{agent.metrics.totalQueries.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">Total Queries</p></CardContent></Card>
              <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-lg font-bold">{agent.metrics.avgResponseMs}ms</p><p className="text-[10px] text-muted-foreground">Avg Response</p></CardContent></Card>
              <Card><CardContent className="pt-3 pb-2 text-center"><p className={cn('text-lg font-bold', agent.metrics.successRate >= 0.9 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>{Math.round(agent.metrics.successRate * 100)}%</p><p className="text-[10px] text-muted-foreground">Success Rate</p></CardContent></Card>
              <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-lg font-bold">{Math.round(agent.metrics.satisfactionScore * 100)}%</p><p className="text-[10px] text-muted-foreground">Satisfaction</p></CardContent></Card>
            </div>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Top Tools Used</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {agent.tools.slice(0, 5).map((tool, i) => (
                    <div key={tool} className="flex items-center gap-3">
                      <span className="text-xs font-mono flex-1 truncate">{tool}</span>
                      <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${100 - i * 15}%` }} />
                      </div>
                      <span className="text-[10px] text-muted-foreground w-10 text-right">{Math.round((100 - i * 15) * agent.metrics.totalQueries / 100)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'settings' && (
          <>
            <Card>
              <CardContent className="pt-4 grid grid-cols-2 gap-4">
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Visibility</p><p className="text-sm capitalize">{agent.visibility.replace('_', ' ')}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Created By</p><p className="text-sm">{agent.createdByName ?? agent.createdBy}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Created</p><p className="text-sm">{new Date(agent.createdAt).toLocaleDateString()}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Updated</p><p className="text-sm">{new Date(agent.updatedAt).toLocaleDateString()}</p></div>
              </CardContent>
            </Card>
            {agent.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">Tags</h3>
                <div className="flex flex-wrap gap-1.5">
                  {agent.tags.map(tag => <Badge key={tag} variant="secondary" className="text-[9px]">{tag}</Badge>)}
                </div>
              </div>
            )}
            {!agent.isBuiltin && (
              <Card className="border-red-500/20">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-red-600 dark:text-red-400">Danger Zone</CardTitle></CardHeader>
                <CardContent className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 dark:text-red-400 border-red-500/30"
                    disabled={updateAgent.isPending}
                    onClick={() => {
                      // Cluster C-STUB-TOAST (Pod-S) — wire to PATCH
                      // /custom-agents/:id with status='archived' so
                      // the agent is actually flipped server-side.
                      updateAgent.mutate(
                        { id: agent.id, body: { status: 'archived' } },
                        {
                          onSuccess: () => {
                            toast.success(`${agent.name} archived`);
                            setStatus('archived');
                          },
                          onError: (e: Error) =>
                            toast.error(`Archive failed: ${e.message}`),
                        },
                      );
                    }}
                  >
                    {updateAgent.isPending ? 'Archiving…' : 'Archive Agent'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deleteAgent.isPending}
                    onClick={() => {
                      if (
                        !confirm(
                          `Delete "${agent.name}"? This action cannot be undone.`,
                        )
                      )
                        return;
                      // Cluster C-STUB-TOAST (Pod-S) — wire to DELETE
                      // /custom-agents/:id. Navigate only inside
                      // onSuccess so a failed delete leaves the
                      // operator on the detail page with a real error.
                      deleteAgent.mutate(
                        { id: agent.id },
                        {
                          onSuccess: () => {
                            toast.success(`${agent.name} deleted`);
                            navigate({ to: '/agents' });
                          },
                          onError: (e: Error) =>
                            toast.error(`Delete failed: ${e.message}`),
                        },
                      );
                    }}
                  >
                    {deleteAgent.isPending ? 'Deleting…' : 'Delete Agent'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
